import { useCallback, useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

const KEY = 'bs-admin-theme';
const listeners = new Set<() => void>();

/**
 * Theme is a display preference with no bearing on access, so it belongs in
 * `localStorage` and outlives the tab. The session deliberately does not — see
 * `auth/session.ts`. Keeping the two in different stores is the point.
 */
function read(): Theme {
  try {
    const stored = window.localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(KEY, theme);
  } catch {
    /* private mode: the class still applies for this tab */
  }
  document.documentElement.classList.toggle('dark', theme === 'dark');
  for (const listener of listeners) listener();
}

export function useTheme(): { theme: Theme; toggle: () => void } {
  const theme = useSyncExternalStore<Theme>(subscribe, read, () => 'light');
  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme]);
  return { theme, toggle };
}
